import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Box from '@mui/material/Box';
import { type SxProps, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode } from 'react';

import { AppLink, type AppLinkProps, InfoHint, VisuallyHidden } from '@/components/atoms';
import { type PhoneNumber } from '@/config/company';
import { phoneWhatsAppSeats } from '@/config/whatsappContacts';
import { whatsappOnlyPhone } from '@/content/site';
import { useWhatsAppLink } from '@/hooks/useWhatsAppLink';
import { isWhatsAppOnly } from '@/utils/phoneChannels';

type Props = {
  phone: PhoneNumber;
  /**
   * The number as this caller lays it out — `+256 … · Uganda` in the footer,
   * `+256 … (Uganda)` in the drawer. Owned by the caller on purpose: this
   * component decides what a number *is* (a call or a chat) and how that is
   * explained, never how its line of text is arranged.
   */
  children: ReactNode;
  sx?: SxProps<Theme> | undefined;
  variant?: AppLinkProps['variant'] | undefined;
  /**
   * Drop the visible `WhatsApp only` label, leaving the icon and the hint.
   * For a row too tight to carry it; the hint still explains the line and the
   * icon still says which app opens.
   */
  hideBadge?: boolean | undefined;
};

/** Self-contrasting, which is why it is a filled chip and not a green glyph. */
const mark = {
  flexShrink: 0,
  width: 20,
  height: 20,
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  bgcolor: 'whatsapp.main',
  color: 'whatsapp.contrastText',
} as const;

const badgeSx = {
  flexShrink: 0,
  px: 1.5,
  py: 0.25,
  borderRadius: 1,
  bgcolor: 'surfaceVariant',
  color: 'text.secondary',
  fontSize: '0.6875rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
} as const;

const rowSx = { display: 'inline-flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' } as const;

/** The label and its hint, kept on the same line as each other. */
const pairSx = { display: 'inline-flex', alignItems: 'center', flexShrink: 0 } as const;

/**
 * The caller's styling, with the row's own layout asserted over it.
 *
 * Order is the point, and it is the opposite of the usual one. A caller's `sx`
 * normally wins, but these callers pass link styles written for a one-line
 * text link — `footerLinkSx` sets `display: inline-block` — and this row has
 * an icon to sit beside. So colour, hover and transform come from the caller
 * and the few layout declarations come last. Flattened rather than nested
 * because MUI's `sx` array takes style objects, not arrays.
 */
const withRowLayout = (sx: SxProps<Theme> | undefined) =>
  [...(Array.isArray(sx) ? sx : [sx ?? false]), rowSx] as SxProps<Theme>;

/**
 * The WhatsApp mark, then the number, then why it is not a call.
 *
 * The icon is a filled green chip rather than a green glyph because of the
 * contrast rule `WhatsAppButton` already documents: the brand green measures
 * around 2:1 on this site's light surfaces, which fails the 3:1 a meaningful
 * non-text graphic owes, so the mark supplies its own background and stops
 * caring what it sits on.
 *
 * Three ways of saying the same thing, deliberately, because each reaches
 * someone the others miss: the icon is read at a glance, the `WhatsApp only`
 * label needs neither a cursor nor a tap, and the hint carries the sentence
 * that answers *why* — in the accessible tree whether or not it is open, since
 * `InfoHint` keeps its words as the trigger's description.
 */
function WhatsAppOnlyPhone({ phone, children, sx, variant, hideBadge }: Props) {
  const href = useWhatsAppLink({
    message: whatsappOnlyPhone.message,
    contactId: phoneWhatsAppSeats[phone.id],
  });

  const label = (
    <>
      <Box component="span" sx={mark} aria-hidden>
        <WhatsAppIcon sx={{ fontSize: 13 }} />
      </Box>
      {children}
    </>
  );

  return (
    <Box sx={rowSx}>
      {href ? (
        /* No `target`/`rel` here: AppLink opens any http(s) link in a new tab
           with the right rel already. `variant` is spread rather than passed,
           because `exactOptionalPropertyTypes` makes an explicit `undefined`
           a different thing from an absent prop. */
        <AppLink href={href} sx={withRowLayout(sx)} {...(variant ? { variant } : {})}>
          {label}
          <VisuallyHidden> (opens WhatsApp in a new tab)</VisuallyHidden>
        </AppLink>
      ) : (
        /* WhatsApp switched off site-wide (`company.whatsappE164`). The other
           controls vanish with it; a published contact detail does not, because
           the number is still a fact about the business — it just stops being
           something this page can open for you. */
        <Typography component="span" sx={withRowLayout(sx)} {...(variant ? { variant } : {})}>
          {label}
        </Typography>
      )}
      {/* One group, so the two wrap together. The footer's contact card is
          narrow enough that this row breaks, and a hint button that wrapped
          on its own sat under the number looking like a stray glyph. Kept as
          a pair it breaks to a second line as a labelled chip with its
          explanation beside it, which reads as a caption rather than a
          mistake. */}
      <Box component="span" sx={pairSx}>
        {!hideBadge && (
          <Box component="span" sx={badgeSx}>
            {whatsappOnlyPhone.badge}
          </Box>
        )}
        {phone.note && <InfoHint label={`the ${phone.country} line`}>{phone.note}</InfoHint>}
      </Box>
    </Box>
  );
}

/**
 * One published number, as whatever it can actually be reached on.
 *
 * The single place that turns a `PhoneNumber` into a control. It exists
 * because the Japan line is published on three surfaces — the footer card, the
 * mobile drawer and /contact — and was a `tel:` link on all three, to a desk
 * that takes no calls. Fixing that at each site would have left three
 * descriptions of the same number, free to drift; this way `channels` in
 * `config/company.ts` decides, and the next number added gets the right
 * affordance on every surface without any of them being touched.
 *
 * A callable number renders exactly as it always did, so the two Uganda lines
 * are untouched by any of this.
 */
export function PhoneChannelLink({ phone, children, sx, variant, hideBadge }: Props) {
  if (isWhatsAppOnly(phone))
    return (
      <WhatsAppOnlyPhone phone={phone} sx={sx} variant={variant} hideBadge={hideBadge}>
        {children}
      </WhatsAppOnlyPhone>
    );

  return (
    <AppLink href={`tel:${phone.e164}`} {...(variant ? { variant } : {})} {...(sx ? { sx } : {})}>
      {children}
    </AppLink>
  );
}
